// Application Insights configuration for monitoring

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

class AppInsightsService {
  private appInsights: ApplicationInsights | null = null;
  private reactPlugin: ReactPlugin | null = null;

  initialize(connectionString?: string) {
    // Use provided connection string or get from config
    if (!connectionString) {
      // Only log in development mode
      if (import.meta.env.DEV) {
        console.log('Application Insights: No connection string found, monitoring disabled');
      }
      return;
    }

    try {
      this.reactPlugin = new ReactPlugin();
      
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: connectionString,
          extensions: [this.reactPlugin],
          extensionConfig: {
            [this.reactPlugin.identifier]: {
              history: null // We'll set this up with React Router if needed
            }
          },
          enableAutoRouteTracking: true,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          enableAjaxErrorStatusText: true,
          enableUnhandledPromiseRejectionTracking: true
        }
      });

      this.appInsights.loadAppInsights();
      this.appInsights.trackPageView(); // Initial page view

      console.log('Application Insights initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Application Insights:', error);
    }
  }

  // Track custom events
  trackEvent(name: string, properties?: { [key: string]: any }) {
    if (this.appInsights) {
      this.appInsights.trackEvent({ name, properties });
    }
  }

  // Track exceptions
  trackException(error: Error, properties?: { [key: string]: any }) {
    if (this.appInsights) {
      this.appInsights.trackException({ exception: error, properties });
    }
  }

  // Track custom metrics
  trackMetric(name: string, average: number, properties?: { [key: string]: any }) {
    if (this.appInsights) {
      this.appInsights.trackMetric({ name, average, properties });
    }
  }

  // Track page views manually
  trackPageView(name?: string, uri?: string) {
    if (this.appInsights) {
      this.appInsights.trackPageView({ name, uri });
    }
  }

  // Track dependencies (API calls, etc.)
  trackDependency(id: string, _method: string, absoluteUrl: string, pathName: string, totalTime: number, success: boolean, resultCode?: number) {
    if (this.appInsights) {
      this.appInsights.trackDependencyData({
        id,
        target: absoluteUrl,
        name: pathName,
        duration: totalTime,
        success,
        responseCode: resultCode || 0
      });
    }
  }

  // Get the React plugin for HOC usage
  getReactPlugin() {
    return this.reactPlugin;
  }

  // Check if Application Insights is enabled
  isEnabled(): boolean {
    return this.appInsights !== null;
  }
}

export const appInsights = new AppInsightsService();
